/**
 * Generation job progress: polls GET /jobs/{id} every 3 s (useJob) and shows a progress bar, then the resulting
 * sounds. On success the team's sound lists are refreshed so the new tracks appear in My sounds.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import type { StudioJob } from '@/api/audioStudioApi';
import { StudioTrackList } from './StudioTrackList';
import { audioKeys, isJobActive, useJob } from './queries';

const STATUS_TEXT: Record<StudioJob['status'], string> = {
  queued: 'Queued — starting soon…',
  running: 'Composing your sound…',
  succeeded: 'Ready',
  failed: 'Generation failed',
};

export function JobProgress({ jobId, initial, onDismiss, onDone, showResults = true, compact = false, label }: {
  jobId: string;
  /** What was asked for — shown under the status. */
  label?: string;
  initial?: StudioJob;
  onDismiss?: () => void;
  onDone?: (job: StudioJob) => void;
  showResults?: boolean;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const q = useJob(jobId);
  const job = q.data ?? initial;
  const notified = useRef(false);

  useEffect(() => {
    if (!job || isJobActive(job) || notified.current) return;
    notified.current = true;
    if (job.status === 'succeeded') void qc.invalidateQueries({ queryKey: audioKeys.tracksAll });
    onDone?.(job);
  }, [job, qc, onDone]);

  const status = job?.status ?? 'queued';
  const pct = Math.round(Math.min(1, Math.max(0, job?.progress ?? 0)) * 100);
  const active = isJobActive(job);

  return (
    <div className="rounded-2xl border border-[#eaecf0] bg-white">
      <div className={compact ? 'px-3 py-2.5' : 'px-4 py-3'}>
        <div className="flex items-center gap-2 text-[13px]" role="status" aria-live="polite">
          {active ? <Loader2 className="h-4 w-4 animate-spin text-[#be43ff]" aria-hidden /> : status === 'succeeded' ? <CheckCircle2 className="h-4 w-4 text-[#12b76a]" aria-hidden /> : <AlertCircle className="h-4 w-4 text-[#d92d20]" aria-hidden />}
          <span className="min-w-0 flex-1 text-[#101828]" style={{ fontWeight: 600 }}>
            {STATUS_TEXT[status]}
            {job && job.requested > 1 && active && <span className="ml-1 font-normal text-[#667085]">({job.requested} variations)</span>}
          </span>
          {active && <span className="tabular-nums text-[12px] text-[#667085]">{pct}%</span>}
          {!active && onDismiss && (
            <button type="button" onClick={onDismiss} aria-label="Dismiss" className="rounded p-1 text-[#98a2b3] hover:bg-[#f2f4f7] hover:text-[#344054]"><X className="h-3.5 w-3.5" aria-hidden /></button>
          )}
        </div>
        {label && <p className="mt-0.5 truncate pl-6 text-[11.5px] text-[#667085]" title={label}>{label}</p>}
        {active && (
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#f2f4f7]" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Generation progress">
            <div className="h-full rounded-full bg-gradient-to-r from-[#be43ff] to-[#7c3aed] transition-[width] duration-700" style={{ width: `${Math.max(pct, 4)}%` }} />
          </div>
        )}
        {status === 'failed' && <p className="mt-1.5 text-[12px] text-[#b42318]">{job?.error || 'Something went wrong. Try again, or tweak the prompt.'}</p>}
        {/* Partial success: some variations failed but the job still succeeded. */}
        {status === 'succeeded' && job?.error && <p className="mt-1.5 text-[12px] text-[#b54708]">Some variations could not be made: {job.error}</p>}
        {q.isError && !job && <p className="mt-1.5 text-[12px] text-[#b42318]">Could not check the job: {(q.error as Error).message}</p>}
      </div>
      {showResults && status === 'succeeded' && job && job.tracks.length > 0 && (
        <div className="border-t border-[#f2f4f7]">
          <StudioTrackList tracks={job.tracks} showOwner={false} />
        </div>
      )}
    </div>
  );
}
